/** @file SplitSheetDialog: an exploded sheet's islands mapped to parts and pivots, then one fill batch. */
import { islandImage } from '@zilo/pose3d-image-io/island/islandImage';
import type { Puppet } from '@zilo/pose3d-format/types/puppet';
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { ArtModal } from '../ArtModal';
import { pivotProposal } from './pivotProposal';
import { proposeTargets, type IslandTarget } from './proposeTargets';
import { restPlacementOf } from './restPlacementOf';
import { SHEET_FACINGS } from './sheetFacings';
import { SheetOverlay } from './SheetOverlay';
import { splitCuts } from './splitCuts';
import type { CutPart } from './sheetTypes';
import { useImageUrls } from './useImageUrls';
import type { SplitState } from './useSheetImport';

/** The sheet being split, the puppet, whether a fill runs, and the fill / close callbacks. */
export type SplitSheetDialogProps = { split: SplitState; puppet: Puppet; busy: boolean; onFill: (cuts: CutPart[]) => void; onClose: () => void };

const BUTTON = { padding: '3px 10px', fontSize: 12 };
const SELECT = { fontSize: 12, background: '#15171b', color: '#e6e8ec', border: '1px solid #44474f', borderRadius: 4 };

/**
 * Left: the sheet with the islands numbered and their pivots (drag to move). Right: the facing for the whole sheet,
 * then one row per island (thumbnail, number, target select with `unused`); `Fill N slots` sends one batch.
 */
export function SplitSheetDialog({ split, puppet, busy, onFill, onClose }: SplitSheetDialogProps): ReactElement {
  const [facing, setFacing] = useState(split.facingDeg);
  const placement = useMemo(() => restPlacementOf(puppet, facing, true), [puppet, facing]);
  const proposed = useMemo(() => proposeTargets(split.map.islands, placement), [split.map, placement]);
  const [edits, setEdits] = useState<{ of: IslandTarget[]; targets: IslandTarget[] } | null>(null);
  const targets = edits?.of === proposed ? edits.targets : proposed;
  const change = (k: number, next: Partial<IslandTarget>): void => {
    setEdits({ of: proposed, targets: targets.map((t, i) => (i === k ? { ...t, ...next } : t)) });
  };
  const thumbs = useMemo(() => proposed.map((t) => islandImage(split.sheet, split.map, t.island)), [split, proposed]);
  const thumbUrls = useImageUrls(thumbs);
  const [sheetUrl] = useImageUrls(useMemo(() => [split.sheet], [split.sheet]));
  const { cuts, duplicates } = useMemo(() => splitCuts(split.sheet, split.map, targets, placement.sampling), [split, targets, placement.sampling]);
  const choose = (k: number, drawable: string): void => {
    const part = placement.parts.find((p) => p.drawable === drawable) ?? null;
    const t = targets[k];
    if (t !== undefined) change(k, { part, pivot: pivotProposal(t.island, part) });
  };
  return (
    <ArtModal label="Split sheet" wide>
      <strong>Split sheet <code>{split.name}</code>: {targets.length} islands</strong>
      <div style={{ display: 'flex', gap: 12, minHeight: 0 }}>
        <SheetOverlay url={sheetUrl ?? ''} width={split.sheet.width} height={split.sheet.height} targets={targets} onPivot={(k, pivot) => { change(k, { pivot }); }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 260, overflow: 'auto' }}>
          <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            Facing
            <select aria-label="Sheet facing" value={facing} style={SELECT} onChange={(e) => { setFacing(Number(e.target.value)); }}>
              {SHEET_FACINGS.map((f) => <option key={f.deg} value={f.deg}>{f.label} {f.deg}</option>)}
            </select>
          </label>
          {targets.map((t, k) => (
            <div key={t.island.id} data-testid="island-row" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ width: 18, textAlign: 'right' }}>{k + 1}</span>
              <span style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#15171b' }}>
                {thumbUrls[k] === '' ? null : <img src={thumbUrls[k]} alt={`island ${String(k + 1)}`} style={{ maxWidth: 40, maxHeight: 40 }} />}
              </span>
              <select aria-label={`Island ${String(k + 1)} target`} value={t.part?.drawable ?? ''} style={SELECT} onChange={(e) => { choose(k, e.target.value); }}>
                <option value="">unused</option>
                {placement.parts.map((p) => <option key={p.drawable} value={p.drawable}>{p.drawable} ({p.slot})</option>)}
              </select>
            </div>
          ))}
          {duplicates.length === 0 ? null : <span role="alert" style={{ color: '#ff8a8a' }}>Chosen twice: {duplicates.join(', ')}</span>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button type="button" style={BUTTON} onClick={onClose}>Cancel</button>
        <button type="button" style={BUTTON} disabled={busy || cuts.length === 0 || duplicates.length > 0} onClick={() => { onFill(cuts); }}>Fill {cuts.length} slots</button>
      </div>
    </ArtModal>
  );
}
